import { ChangeDetectorRef, Component, ChangeDetectionStrategy, Optional } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConversationListService, UserSearchResult } from '../../services/conversation-list.service';

export interface NewConversationDialogData {
  isStream?: boolean;
}

@Component({
  selector: 'app-new-conversation-dialog',
  templateUrl: './new-conversation-dialog.component.html',
  styleUrls: ['./new-conversation-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class NewConversationDialogComponent {
  isStream = false;
  selectedUsers: UserSearchResult[] = [];
  suggestions: UserSearchResult[] = [];
  streamName = '';
  creating = false;

  constructor(
    public ref: DynamicDialogRef,
    @Optional() private dialogConfig: DynamicDialogConfig<NewConversationDialogData>,
    private conversationListService: ConversationListService,
    private cdr: ChangeDetectorRef
  ) {
    this.isStream = !!this.dialogConfig?.data?.isStream;
  }

  get canStart(): boolean {
    return this.selectedUsers.length > 0 && (!this.isStream || !!this.streamName.trim());
  }

  // The CloudLink SDK call underneath resolves outside Angular's zone, so under OnPush
  // nothing schedules a check when it completes - the DOM (and p-autoComplete's own loading
  // spinner, which it only clears once it sees [suggestions] change) is left stale until some
  // unrelated event forces a check. Force one explicitly once the result is in.
  async search(event: { query: string }): Promise<void> {
    this.suggestions = await this.conversationListService.searchUsers(event.query);
    this.cdr.markForCheck();
  }

  async start(): Promise<void> {
    if (!this.canStart || this.creating) {
      return;
    }

    this.creating = true;
    this.cdr.markForCheck();
    try {
      const userIds = this.selectedUsers.map(user => user.userId);
      const conversationId = this.isStream
        ? await this.conversationListService.createConversation(userIds, this.streamName.trim())
        : await this.conversationListService.createChatWithUserIds(userIds);
      this.ref.close(conversationId);
    } finally {
      this.creating = false;
      this.cdr.markForCheck();
    }
  }
}
